import { Component, OnInit } from '@angular/core';
import { DialogRef } from '@fundamental-ngx/core';
import { SavedFilterView, SavedFilterViewsService } from "../../../service/services/misc/saved-filter-views/saved-filter-views.service";

interface ManageableView extends SavedFilterView {
    isSystem?: boolean;
    originalName?: string;
    errorMessage?: string;
}

@Component({
    selector: 'app-manage-filter-views-dialog',
    templateUrl: './manage-filter-views-dialog.component.html',
    styleUrls: ['./manage-filter-views-dialog.component.scss'],
    standalone: false
})
export class ManageFilterViewsDialogComponent implements OnInit {
    views: ManageableView[] = [];
    searchTerm: string = '';
    defaultViewName: string = '';
    organizationPath?: string;
    deletedViews: string[] = [];
    renamedViews: Map<string, string> = new Map();

    constructor(
        public dialogRef: DialogRef,
        private savedFilterViewsService: SavedFilterViewsService
    ) {
        // Get organization path from dialog config data if provided
        this.organizationPath = this.dialogRef.data?.organizationPath;
    }

    ngOnInit(): void {
        this.loadViews();
    }

    loadViews(): void {
        const allViews = this.savedFilterViewsService.getAllViews();

        // Add "Standard" as a system view
        this.views = [
            {
                name: 'Standard',
                filters: { recursiveSearch: true },
                createdAt: new Date().toISOString(),
                isSystem: true,
                isDefault: false
            } as ManageableView,
            ...allViews.map(v => ({
                ...v,
                isSystem: false,
                originalName: v.name
            } as ManageableView))
        ];

        // Find the default view for this organization path
        const defaultView = this.savedFilterViewsService.getDefaultView(this.organizationPath);
        if (defaultView) {
            this.defaultViewName = defaultView.name;
        } else {
            this.defaultViewName = 'Standard';
        }
    }

    get filteredViews(): ManageableView[] {
        if (!this.searchTerm.trim()) {
            return this.views;
        }

        const term = this.searchTerm.toLowerCase();
        return this.views.filter(v =>
            v.name.toLowerCase().includes(term)
        );
    }

    onViewNameChanged(view: ManageableView): void {
        // Clear error message
        view.errorMessage = '';

        const trimmedName = view.name.trim();

        // Check for empty name
        if (!trimmedName) {
            view.errorMessage = 'View name cannot be empty';
            return;
        }

        // Check for duplicates against other views in the list
        const duplicate = this.views.find(v =>
            v !== view && v.name.trim().toLowerCase() === trimmedName.toLowerCase()
        );
        if (duplicate) {
            view.errorMessage = 'This view name already exists. Please enter a different name';
            return;
        }

        // Track renamed views
        if (view.originalName && view.name !== view.originalName) {
            this.renamedViews.set(view.originalName, view.name);
        }
    }

    onDeleteView(view: ManageableView): void {
        if (view.isSystem) {
            return;
        }

        // Track deleted view
        if (view.originalName) {
            this.deletedViews.push(view.originalName);
        }

        // Remove from display list
        const index = this.views.indexOf(view);
        if (index > -1) {
            this.views.splice(index, 1);
        }

        // If this was the default, reset to Standard
        if (this.defaultViewName === view.name || this.defaultViewName === view.originalName) {
            this.defaultViewName = 'Standard';
        }

    }

    isValid(): boolean {
        // Check for duplicate names
        const names = this.views.map(v => v.name.trim().toLowerCase());
        const uniqueNames = new Set(names);
        if (names.length !== uniqueNames.size) {
            return false;
        }

        // Check for empty names and error messages
        return this.views.every(v => v.name && v.name.trim().length > 0 && !v.errorMessage);
    }

    onSave(): void {
        if (!this.isValid()) {
            alert('Please ensure all view names are unique and not empty.');
            return;
        }

        // Delete views that were marked for deletion
        this.deletedViews.forEach(viewName => {
            this.savedFilterViewsService.deleteView(viewName);
        });

        // Handle renames and default changes
        this.views.forEach(view => {
            if (view.isSystem) {
                return;
            }

            const isDefault = this.defaultViewName === view.name;
            const originalName = view.originalName || view.name;

            // If renamed, delete old and create new
            if (originalName !== view.name) {
                this.savedFilterViewsService.deleteView(originalName);
                this.savedFilterViewsService.saveView(
                    view.name,
                    view.filters,
                    this.organizationPath,
                    isDefault
                );
            } else {
                // Just update default status if needed
                const existingView = this.savedFilterViewsService.getView(view.name);
                if (existingView && existingView.isDefault !== isDefault) {
                    this.savedFilterViewsService.saveView(
                        view.name,
                        view.filters,
                        this.organizationPath,
                        isDefault
                    );
                }
            }
        });

        // Return the selected default view name
        this.dialogRef.close(this.defaultViewName);
    }
}
